import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Database, Brain, Rocket, TrendingUp, Search } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const Resources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Resources", url: "/resources" }
  ];

  const filters = ['All', 'Data Preparation', 'Model Evaluation', 'Production Scaling', 'Generative AI'];

  const articles = [
    {
      title: 'Why Lab Accuracy is a Vanity Metric',
      category: 'Model Evaluation',
      description: 'Understanding why 99% lab accuracy doesn\'t guarantee real-world AI performance. Learn about distribution shift, overfitting, and edge case bottlenecks.',
      date: 'Dec 16, 2024',
      readTime: '6 min read',
      image: '/tiger-dog-vanity-metric-new.jpg',
      icon: TrendingUp,
      slug: 'vanity-metric'
    },
    {
      title: 'Mitigating Concept Drift: A Data-Centric Approach',
      category: 'Model Evaluation',
      description: 'Learn how high-quality training data and continuous evaluation can help prevent and detect concept drift in production AI systems.',
      date: 'Dec 15, 2024',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
      icon: TrendingUp,
      slug: 'concept-drift'
    },
    {
      title: 'The Hidden Cost of Low-Quality Training Data',
      category: 'Data Preparation',
      description: 'Discover why investing in production-ready annotation from the start saves time, money, and reputation in the long run.',
      date: 'Dec 10, 2024',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86',
      icon: Database,
      slug: 'training-data-quality'
    },
    {
      title: 'RLHF Best Practices for LLM Deployment',
      category: 'Generative AI',
      description: 'A comprehensive guide to reinforcement learning from human feedback and how to implement it effectively for production LLMs.',
      date: 'Dec 5, 2024',
      readTime: '10 min read',
      image: 'https://images.pexels.com/photos/6019019/pexels-photo-6019019.jpeg',
      icon: Brain,
      slug: 'rlhf-best-practices'
    },
    {
      title: 'Scaling Human-in-the-Loop AI: From Pilot to Production',
      category: 'Production Scaling',
      description: 'Strategic insights on maintaining quality while scaling your AI operations from small pilots to enterprise-wide deployment.',
      date: 'Nov 28, 2024',
      readTime: '7 min read',
      image: 'https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg',
      icon: Rocket,
      slug: 'scaling-hitl-ai'
    },
    {
      title: 'Edge Case Management in Production AI',
      category: 'Model Evaluation',
      description: 'How to identify, annotate, and handle edge cases to ensure your AI models perform reliably in unexpected scenarios.',
      date: 'Nov 20, 2024',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f',
      icon: TrendingUp,
      slug: 'edge-case-management'
    },
    {
      title: 'LiDAR Annotation for Autonomous Systems',
      category: 'Data Preparation',
      description: 'Technical deep-dive into 3D point cloud annotation techniques for sensor fusion in autonomous vehicles.',
      date: 'Nov 15, 2024',
      readTime: '12 min read',
      image: 'https://images.unsplash.com/photo-1694729101068-a2c621f877b4',
      icon: Database,
      slug: 'lidar-annotation'
    },
    {
      title: 'Quality Metrics That Matter for Production AI',
      category: 'Model Evaluation',
      description: 'Beyond accuracy: the quality metrics you should track to ensure your AI models are truly production-ready.',
      date: 'Nov 8, 2024',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28',
      icon: TrendingUp,
      slug: 'quality-metrics'
    },
    {
      title: 'Building Annotation Workflows That Scale',
      category: 'Production Scaling',
      description: 'Learn how to design annotation pipelines that maintain quality while scaling to meet enterprise demands.',
      date: 'Nov 1, 2024',
      readTime: '7 min read',
      image: 'https://images.pexels.com/photos/17485738/pexels-photo-17485738.png',
      icon: Rocket,
      slug: 'annotation-workflows'
    },
    {
      title: 'The Future of Multimodal AI Data Preparation',
      category: 'Data Preparation',
      description: 'Exploring emerging challenges and solutions in preparing training data for next-generation multimodal AI systems.',
      date: 'Oct 25, 2024',
      readTime: '10 min read',
      image: 'https://images.pexels.com/photos/18069488/pexels-photo-18069488.png',
      icon: Database,
      slug: 'multimodal-ai-data'
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesFilter = selectedFilter === 'All' || article.category === selectedFilter;
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         article.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title="AI Resources & Best Practices"
        description="Expert insights on AI data operations: annotation best practices, model evaluation, RLHF, training data quality, and production scaling strategies."
        canonical="/resources"
        type="website"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Navigating the <span className="text-[#379F7F]">AI Model Lifecycle</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Expert insights, best practices, and technical guides to help you achieve 
              data certainty and deploy production-ready AI models.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-gray-800 border-gray-700 text-white h-12 text-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-black border-b border-gray-800 sticky top-16 z-40 backdrop-blur-md bg-black/90">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <Badge
                key={filter}
                variant={selectedFilter === filter ? 'default' : 'outline'}
                className={`cursor-pointer px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                  selectedFilter === filter
                    ? 'bg-[#379F7F] text-black hover:bg-[#379F7F]/90'
                    : 'border-gray-700 text-gray-300 hover:border-[#379F7F] hover:text-[#379F7F]'
                }`}
                onClick={() => setSelectedFilter(filter)}
              >
                {filter}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 bg-black">
        <div className="container mx-auto px-6">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <Link 
                  key={index} 
                  to={article.slug ? `/resources/${article.slug}` : '#'}
                  className={`block h-full ${!article.slug ? 'pointer-events-none' : ''}`}
                >
                  <Card 
                    className="bg-gray-800 border-gray-700 hover:border-[#379F7F] transition-all duration-300 overflow-hidden group h-full flex flex-col cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <LazyImage 
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-800 to-transparent"></div>
                      <div className="absolute top-4 right-4">
                        <article.icon className="w-8 h-8 text-[#379F7F]" />
                      </div>
                    </div>
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <Badge className="bg-[#379F7F] text-black text-xs">
                          {article.category}
                        </Badge>
                        <span className="text-xs text-gray-400">{article.readTime}</span>
                      </div>
                      <CardTitle className="text-white text-lg group-hover:text-[#379F7F] transition-colors duration-300">
                        {article.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col justify-between">
                      <CardDescription className="text-gray-300 mb-4">
                        {article.description}
                      </CardDescription>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">{article.date}</span>
                        {article.slug ? (
                          <span className="text-[#379F7F] text-sm font-medium">
                            Read More →
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-xl">No articles found matching your criteria.</p>
              <Button 
                variant="outline"
                className="mt-6 border-[#379F7F] text-[#379F7F] hover:bg-[#379F7F] hover:text-black"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedFilter('All');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stay Updated on <span className="text-[#379F7F]">AI Model Lifecycle Best Practices</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the latest insights, case studies, and technical guides delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <Input
                type="email"
                placeholder="Your email address"
                className="flex-1 bg-gray-900 border-gray-700 text-white h-12"
              />
              <Button 
                size="lg"
                className="bg-[#379F7F] text-black hover:bg-[#379F7F]/90 font-semibold px-8"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
